import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import app from "../../configs/FirebaseConfig";
import { FIRESTORE_USERS_TABLE } from "../../constants/FirestoreConstant";
import { CMS_ROLES } from "../../functions/permissions";
import { report } from "../../functions/api/guardian";
import { useAuth } from "./useAuth";

/**
 * Eén bron voor de `users`-collectie, met drie eigen listeners.
 *
 * **Twee dingen liggen hier vast. Verander ze niet zonder de historie te lezen.**
 *
 * 1. GEEN useFirestoreConnect. redux-firestore kiest per snapshot tussen één
 *    bulk-actie en één actie per document (`utils/query.js`,
 *    dispatchListenerResponse):
 *
 *        if (docChanges && docChanges.length < docData.size) { per document }
 *
 *    De AuthProvider (./useAuth.js) zet het eigen profiel `users/{uid}` al
 *    apart in de cache, dus dat ene document is in de eerste snapshot géén
 *    "change" en de hele collectie gaat het per-document pad in. Gemeten met
 *    2332 gebruikers: 2331 dispatches en ~45 seconden geblokkeerde hoofdthread.
 *    Een eigen onSnapshot krijgt diezelfde snapshot als één callback.
 *
 * 2. WEL dezelfde drie filters als vroeger. Firestore beoordeelt een
 *    list-query per document: een regel in de vorm
 *    `isAdmin() || resource.data.role == 'webshopUser'` laat een ongefilterde
 *    list slagen voor een beheerder, maar weigert hem volledig voor een
 *    verkoper — de CMS-documenten voldoen dan niet. Een ongefilterde read
 *    werkte daarom wél voor cmsAdmin en gaf cmsSales een lege lijst.
 *    De drie queries hieronder zijn precies de vormen die er altijd al waren.
 *
 * Zie docs/cms-performance.md en docs/cms-toegang.md.
 *
 * `users` is `undefined` zolang er nog niets binnen is en blijft dat bij een
 * fout — een mislukte read mag er nooit uitzien als "geen klanten". `error`
 * vertelt schermen dat het laden is misgelukt.
 */
const UsersContext = createContext({ users: undefined, error: null });

const QUERIES = [
  { naam: "klanten", maak: (col) => col.where("role", "==", "webshopUser") },
  { naam: "cms-accounts", maak: (col) => col.where("role", "in", CMS_ROLES) },
  { naam: "cms-gekoppelde accounts", maak: (col) => col.where("cmsRole", "in", CMS_ROLES) },
];

export const UsersProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [delen, setDelen] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setDelen({});
      setError(null);
      return undefined;
    }
    setError(null);
    const col = app.firestore().collection(FIRESTORE_USERS_TABLE);
    const unsubscribes = QUERIES.map(({ naam, maak }) =>
      maak(col).onSnapshot(
        (snap) => {
          const docs = snap.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
          setDelen((vorige) => ({ ...vorige, [naam]: docs }));
        },
        (err) => {
          // Nooit stilzwijgend leegmaken: dan lijkt een geweigerde read op
          // "geen resultaten" en zoekt de volgende persoon op de verkeerde plek.
          console.error(`Gebruikers laden mislukt (${naam})`, err);
          setError({ query: naam, code: err?.code, message: err?.message });
          // Precies de melding die bij de vorige bug had moeten komen.
          report({
            severity: "error",
            type: "firestore_read_failed",
            title: `Firestore-read mislukt (${err?.code || "onbekend"}): users/${naam}`,
            context: { query: naam, code: err?.code, melding: err?.message },
          });
        },
      ),
    );
    return () => unsubscribes.forEach((stop) => stop && stop());
  }, [isAuthenticated]);

  const users = useMemo(() => {
    const lijsten = QUERIES.map(({ naam }) => delen[naam]).filter(Array.isArray);
    if (!lijsten.length) return undefined;
    return _.unionBy(...lijsten, "id");
  }, [delen]);

  const waarde = useMemo(() => ({ users, error }), [users, error]);
  return <UsersContext.Provider value={waarde}>{children}</UsersContext.Provider>;
};

export const useAllUsers = () => useContext(UsersContext).users;

/** `null` als alles goed ging; anders `{ query, code, message }`. */
export const useUsersError = () => useContext(UsersContext).error;

/** De webshopklanten. Vervangt de oude store-sleutel `webshopUsers`. */
export const useWebshopUsers = () => {
  const users = useAllUsers();
  return useMemo(() => (Array.isArray(users) ? users.filter((user) => user?.role === "webshopUser") : users), [users]);
};

/** Verkopers. Vervangt de oude store-sleutel `salesPersons`. */
export const useSalesPersons = () => {
  const users = useAllUsers();
  return useMemo(() => (Array.isArray(users) ? users.filter((user) => user?.isSalesPerson === true) : users), [users]);
};

export default useAllUsers;
